import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { Loader, NumberInput, Tooltip } from '../../../components';
import AnimalInfo from '../../../components/animal/AnimalInfo';
import BackButton from '../../../components/back-button/BackButton';
import SpecimenLotInfo from '../../../components/specimen-lot-info/SpecimenLotInfo';
import { InfoSVG } from '../../../components/svgs';
import OnFarmAnimalSelector from './OnFarmAnimalSelector';
import { onFarmDiscardPost } from '../../../api/onFarmTransactionApi';
import {
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { ON_FARM_PATHS, ROUTE_PATHS } from '../../../constants/routePaths';
import { showToast } from '../../../services/toast.service';
import { downloadOnFarmTransactionExport } from '../../../services/onFarmTransactionExport.service';
import { getOnFarmAnimalById, getOnFarmSpecimensByOwnership } from '../../../services/onFarmInventory.service';
import { Animal, OnFarmDiscardSpecimenRequest, SpecimenInventory, SpecimenLocation } from '../../../types/interfaces';
import { Genders, InventorySource, TransactionTypeEnum } from '../../../types/enums';
import { dateFormatMMDDYYYY } from '../../../utils/commonUtils';
import './onFarmInventory.scss';

type DiscardItem = {
  availableQty: number;
  isSelected: boolean;
  quantity: number;
  specimenId: number;
  embryoStrawId?: number;
  selectable?: boolean;
};

type LocationDiscardAmount = {
  key: string;
  location: SpecimenLocation;
  amount: number;
};

const OnFarmDiscard = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();

  const accountId = useAppSelector(state => state.activeAccount.accountId);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>('');
  const [ownership, setOwnership] = useState<SpecimenInventory[]>();
  const [selectedDiscardItems, setSelectedDiscardItems] = useState<DiscardItem[]>([]);
  const [locationDiscardAmounts, setLocationDiscardAmounts] = useState<LocationDiscardAmount[]>([]);
  const [totalAvailableOwnedQty, setTotalAvailableOwnedQty] = useState<number>();
  const [animal, setAnimal] = useState<Animal>();
  const [embryoMissingSpecimenIds, setEmbryoMissingSpecimenIds] = useState<number[]>([]);

  useEffect(() => {
    if (animalId) {
      fetchAnimal();
    } else {
      setAnimal(undefined);
    }
  }, [animalId]);

  const fetchAnimal = async () => {
    try {
      const response = await getOnFarmAnimalById(animalId ?? '', { include: 'Breed.Species' });
      setAnimal(response.data);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const isFemale = animal?.gender === Genders.Female;

  const getSpecimenKey = useCallback(
    (specimen: SpecimenInventory) => {
      if (isFemale && specimen.embryoStrawId) {
        return `embryo-${specimen.embryoStrawId}`;
      }
      return `specimen-${specimen.specimenId}`;
    },
    [isFemale],
  );

  const selectedSpecimens = (ownership ?? [])
    .map((specimen, index) => ({
      specimen,
      discardItem: selectedDiscardItems[index],
      key: getSpecimenKey(specimen),
      index,
    }))
    .filter(row => row.discardItem?.isSelected);

  const totalDiscardQuantity = selectedDiscardItems
    .filter(item => item.isSelected)
    .reduce((accumulator, current) => accumulator + current.quantity, 0);

  const totalLocationDiscardAmounts = locationDiscardAmounts.reduce((total, locationAmount) => {
    return total + (locationAmount.amount || 0);
  }, 0);

  useEffect(() => {
    const handleGetSpecimens = async () => {
      if (animalId && accountId && animal) {
        try {
          const { data } = await getOnFarmSpecimensByOwnership(animalId, accountId);

          let missingEmbryoSpecimenIds: number[] = [];
          if (animal?.gender === Genders.Female) {
            missingEmbryoSpecimenIds = data
              .filter(spec => spec.quantityAvailable > 0 && spec.embryoStraws?.length === 0)
              .map(spec => spec.specimenId);
          }
          setEmbryoMissingSpecimenIds(missingEmbryoSpecimenIds);

          let flattenedOwnership: SpecimenInventory[] = [];
          if (animal?.gender === Genders.Female) {
            flattenedOwnership = data.flatMap((specimen: SpecimenInventory) => {
              if (specimen.embryoStraws && specimen.embryoStraws.length > 0) {
                return specimen.embryoStraws.map(straw => ({
                  ...specimen,
                  embryoStrawCode: straw.embryoStrawCode,
                  embryoStrawNumber: straw.embryoStrawNumber,
                  embryoStrawId: straw.embryoStrawId,
                  quantityAvailable: straw.embryoCountPerStraw ?? 0,
                  embryoGrade: straw.embryoGrade,
                  embryoStage: straw.embryoStage,
                  specimenLocations: (specimen.specimenLocations ?? []).filter(
                    location => !straw.storageCanisterId || location.storageCanisterId === straw.storageCanisterId,
                  ),
                }));
              }
              return specimen;
            });
          } else {
            flattenedOwnership = data;
          }

          flattenedOwnership = flattenedOwnership.filter(item => (item.quantityAvailable ?? 0) > 0);

          setOwnership(flattenedOwnership);
          setTotalAvailableOwnedQty(flattenedOwnership.reduce((acc, curr) => acc + curr.quantityAvailable, 0));
          handleCreateDiscardItems(flattenedOwnership, animal?.gender === Genders.Female);
          setLocationDiscardAmounts([]);
          setIsLoading(false);
        } catch (error) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

    handleGetSpecimens();
  }, [animalId, accountId, animal]);

  useEffect(() => {
    selectedDiscardItems.forEach(inventoryLot => {
      if (inventoryLot.isSelected && inventoryLot.quantity > inventoryLot.availableQty) {
        showToast.error(toastMessages.INVALID_DISCARD_QUANTITY);
      }
    });
  }, [selectedDiscardItems]);

  useEffect(() => {
    setLocationDiscardAmounts(previousLocations => {
      const updatedLocations = [...previousLocations];

      selectedSpecimens.forEach(({ specimen, key: specimenKey, discardItem }) => {
        specimen.specimenLocations?.forEach(specimenLocation => {
          const alreadyExists = updatedLocations.some(
            existingLocation =>
              existingLocation.key === specimenKey &&
              existingLocation.location.storageCanisterId === specimenLocation.storageCanisterId,
          );

          if (!alreadyExists) {
            updatedLocations.push({
              key: specimenKey,
              location: specimenLocation,
              amount: isFemale ? discardItem?.availableQty ?? specimen.quantityAvailable ?? 0 : 0,
            });
          }
        });
      });

      return updatedLocations.filter(existingLocation =>
        selectedSpecimens.some(selectedSpecimen => selectedSpecimen.key === existingLocation.key),
      );
    });
  }, [selectedDiscardItems, ownership, isFemale]);

  const handleCreateDiscardItems = (ownershipItems: SpecimenInventory[], female: boolean) => {
    const discardItems = ownershipItems.map(item => {
      return {
        isSelected: false,
        specimenId: item.specimenId,
        embryoStrawId: item.embryoStrawId,
        quantity: 0,
        availableQty: item.quantityAvailable,
        selectable: female ? !!item.embryoStrawId : true,
      };
    });
    setSelectedDiscardItems(discardItems);
  };

  const isSemenLocationQuantityValid = () => {
    return selectedSpecimens.every(({ discardItem, key }) => {
      if (isFemale || !discardItem) {
        return true;
      }

      const locationTotal = locationDiscardAmounts
        .filter(locationAmount => locationAmount.key === key)
        .reduce((sum, locationAmount) => sum + (locationAmount.amount || 0), 0);

      return discardItem.quantity === locationTotal;
    });
  };

  const isValid = (isSubmit?: boolean) => {
    if (!selectedDiscardItems.find(item => item.isSelected)) {
      isSubmit && showToast.error(toastMessages.NO_ITEM_SELECTED);
      return false;
    } else if (selectedDiscardItems.some(item => item.isSelected && item.quantity > item.availableQty)) {
      isSubmit && showToast.error(toastMessages.INVALID_DISCARD_QUANTITY);
      return false;
    } else if (!totalAvailableOwnedQty || totalDiscardQuantity > totalAvailableOwnedQty) {
      isSubmit && showToast.error(toastMessages.INVALID_DISCARD_QUANTITY);
      return false;
    } else if (totalDiscardQuantity === 0 || selectedDiscardItems.some(item => item.isSelected && item.quantity === 0)) {
      isSubmit && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    } else if (
      locationDiscardAmounts.length === 0 ||
      totalLocationDiscardAmounts !== totalDiscardQuantity ||
      locationDiscardAmounts.some(location => location.amount > location.location.quantity) ||
      !isSemenLocationQuantityValid()
    ) {
      isSubmit && showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const hasQuantityMismatch =
      (totalDiscardQuantity > 0 || totalLocationDiscardAmounts > 0) && totalLocationDiscardAmounts !== totalDiscardQuantity;
    const hasSemenMismatch = !isSemenLocationQuantityValid();

    if (isValid(true)) {
      try {
        setIsLoading(true);

        if (!accountId) {
          throw new Error('Account Id is not defined');
        }

        const mappedDiscardSpecimens: OnFarmDiscardSpecimenRequest[] = locationDiscardAmounts
          .filter(locationAmount => locationAmount.amount > 0)
          .map(locationAmount => {
            const selected = selectedSpecimens.find(row => row.key === locationAmount.key);
            const discardEntry: OnFarmDiscardSpecimenRequest = {
              onFarmSpecimenId: selected?.discardItem.specimenId ?? locationAmount.location.specimenId!,
              quantity: locationAmount.amount,
              selectedCanisterId: locationAmount.location.storageCanisterId,
            };

            if (isFemale && selected?.discardItem.embryoStrawId) {
              discardEntry.selectedEmbryoStrawIds = [selected.discardItem.embryoStrawId];
            }

            return discardEntry;
          });

        const { data: onFarmTransactionId } = await onFarmDiscardPost({
          specimens: mappedDiscardSpecimens,
          notes: notes || undefined,
        });

        await downloadOnFarmTransactionExport(
          {
            onFarmTransactionId,
            specimens: mappedDiscardSpecimens,
          },
          TransactionTypeEnum.Discard,
        );

        showToast.success(toastMessages.DISCARD_SUCCESSFUL);
        navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY);
      } catch (error: any) {
        if (error?.response?.status === 409) {
          try {
            const detailObject = JSON.parse(error.response.data.detail);
            showToast.error(detailObject.ErrMsg || toastMessages.SOMETHING_WENT_WRONG);
          } catch {
            showToast.error(toastMessages.SOMETHING_WENT_WRONG);
          }
        } else {
          showToast.error(error?.response?.data?.title || toastMessages.SOMETHING_WENT_WRONG);
        }
      } finally {
        setIsLoading(false);
      }
    } else if (hasQuantityMismatch || hasSemenMismatch) {
      showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
    }
  };

  const inputCheckbox = (discardItem: DiscardItem | undefined, index: number) => {
    return (
      <input
        type="checkbox"
        name="selected-checkin-specimen-row"
        disabled={discardItem?.selectable === false}
        onChange={e =>
          setSelectedDiscardItems(prev => {
            const newItems = [...prev];
            if (discardItem) {
              const updatedItem = { ...discardItem, isSelected: e.target.checked };
              if (isFemale) {
                updatedItem.quantity = e.target.checked ? discardItem.availableQty : 0;
              } else if (!e.target.checked) {
                updatedItem.quantity = 0;
              }

              newItems[index] = updatedItem;
            }
            return newItems;
          })
        }
        checked={discardItem?.isSelected || false}
      />
    );
  };

  const inputQuantityElement = (discardItem: DiscardItem | undefined, index: number) => {
    const displayValue = isFemale
      ? discardItem?.isSelected
        ? discardItem?.availableQty ?? 0
        : '-'
      : discardItem?.isSelected
      ? discardItem.quantity > 0
        ? discardItem.quantity
        : ''
      : '-';

    return (
      <input
        type="text"
        placeholder="0"
        value={displayValue}
        disabled={isFemale || !discardItem?.isSelected}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (isFemale) return;
          const count = +e.target.value.replace(/[^\d]+/, '').replace(/^0+/, '');
          let newQuantities = [...selectedDiscardItems];
          newQuantities[index] = { ...selectedDiscardItems[index], quantity: count };
          setSelectedDiscardItems(newQuantities);
        }}
      />
    );
  };

  const handleErrors = (discardItem: DiscardItem | undefined, item: SpecimenInventory) => {
    return discardItem?.isSelected
      ? (discardItem?.quantity ?? 0) > item?.quantityAvailable
        ? 'error'
        : discardItem?.quantity === 0
        ? 'warning'
        : ''
      : '';
  };

  const handleLocationRowClass = useCallback(
    (location: SpecimenLocation, key: string): string => {
      const current = locationDiscardAmounts.find(
        locationAmount => locationAmount.key === key && locationAmount.location.storageCanisterId === location.storageCanisterId,
      );
      if (current && current?.amount > location.quantity) {
        showToast.error(toastMessages.CHOSE_MORE_THAN_TOTAL);
        return 'specimen-row invalid-row';
      }
      return 'specimen-row';
    },
    [locationDiscardAmounts],
  );

  const handleSpecimenLocationAmountChange = (key: string, canisterId: number, newAmount: number) => {
    setLocationDiscardAmounts(prev =>
      prev.map(location =>
        location.key === key && location.location.storageCanisterId === canisterId
          ? { ...location, amount: newAmount }
          : location,
      ),
    );
  };

  const renderLocationTableRows = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const rowClass = `${handleLocationRowClass(location, key)} ${isTravelingTank ? 'disabled' : ''}`;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name}/${location.storageCanister?.storageTank?.name}/${location.storageCanister?.name}`;
    const animalDetails = `${specimen.animal?.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationDiscardAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <tr className={rowClass} key={`${key}-loc-${location?.storageCanisterId}`}>
        <td>{locationPath}</td>
        <td>{animalDetails}</td>
        <td>{location.quantity}</td>
        {isFemale && <td>{specimen.embryoStrawCode || '-'}</td>}
        <td>
          {isTravelingTank ? (
            LABEL_CONSTANTS.TRAVELING_TANK
          ) : isFemale ? (
            <NumberInput value={specimen.quantityAvailable} />
          ) : (
            <NumberInput
              onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
              value={currentAmount.toString()}
            />
          )}
        </td>
      </tr>
    );
  };

  const renderLocationMobileRow = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name} / ${location.storageCanister?.storageTank?.name} / ${location.storageCanister?.name}`;
    const animalDetails = `${specimen.animal?.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationDiscardAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <div className="selected-storage-location" key={`${key}-loc-${location.storageCanisterId}`}>
        <div className={`card max-width ${isTravelingTank ? 'disabled' : ''}`}>
          <div className="body">
            <div className="body-content">
              <div className="selected-location">
                <label>{TABLE_HEADER_CONSTANTS.LOCATION}:</label>
                <label>{locationPath}</label>
              </div>
              <div className="location-item">
                <label>{TABLE_HEADER_CONSTANTS.ITEM}:</label>
                <label>{animalDetails}</label>
              </div>
            </div>
            <hr />
            <div className="body-content">
              <div className="canister-qty">
                <label>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</label>
                <label>{location.quantity}</label>
              </div>
              <div className="actual-quantity">
                {isTravelingTank ? (
                  <label>{LABEL_CONSTANTS.TRAVELING_TANK}</label>
                ) : isFemale ? (
                  <>
                    <label>{LABEL_CONSTANTS.QUANTITY_TO_DISCARD}</label>
                    <NumberInput value={specimen.quantityAvailable} />
                  </>
                ) : (
                  <>
                    <label>{LABEL_CONSTANTS.QUANTITY_TO_DISCARD}</label>
                    <NumberInput
                      onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
                      value={currentAmount.toString()}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="on-farm on-farm-use inventory-discard">
      <BackButton />
      <div className="inventory-action card">
        <h1>{PAGE_HEADER_CONSTANTS.DISCARD_INVENTORY}</h1>
        <OnFarmAnimalSelector animalId={animalId} actionPath={ON_FARM_PATHS.DISCARD} />
        {isLoading ? (
          <Loader loaderSize="medium" simple />
        ) : (
          <form action="submit" onSubmit={e => handleSubmit(e)}>
            <AnimalInfo animalId={animalId} showQuantityAvailable inventorySource={InventorySource.ON_FARM} />
            <br />
            <h2>{LABEL_CONSTANTS.SELECT_ITEM_QUANTITY}</h2>
            <div className="desk-select-specimens">
              <div className="specimen-table">
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                      <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_NO}</th>
                      {isFemale ? (
                        <>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                          <th>{TABLE_HEADER_CONSTANTS.FLUSH_MATING}</th>
                        </>
                      ) : (
                        <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                      )}
                      <th>{isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownership?.map((item: SpecimenInventory, index: number) => {
                      const discardItem = selectedDiscardItems.find(selectedItem =>
                        isFemale
                          ? selectedItem.embryoStrawId === item.embryoStrawId
                          : selectedItem.specimenId === item.specimenId,
                      );
                      const flushMating: string = String(
                        item.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')
                          ?.value ?? '-',
                      );

                      return (
                        <tr key={index} className={handleErrors(discardItem, item)}>
                          <td>{inputCheckbox(discardItem, index)}</td>
                          <td>
                            {item.animal?.code}-{item.animal?.name}
                          </td>
                          <td>{item.specimenType?.name}</td>
                          <td>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</td>
                          {isFemale ? (
                            <>
                              <td>
                                <div>
                                  {item.embryoStrawCode ?? '-'}
                                  <Tooltip>
                                    {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`} <br />
                                    {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                  </Tooltip>
                                </div>
                              </td>
                              <td>{item.embryoStrawNumber ?? '-'}</td>
                              <td>{flushMating}</td>
                            </>
                          ) : (
                            <td>{item.quality}</td>
                          )}

                          <td>{item.quantityAvailable}</td>
                          <td>{inputQuantityElement(discardItem, index)}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={isFemale ? 8 : 6}></td>
                      <td colSpan={3}>
                        <div className="quantity">{totalDiscardQuantity}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="xs-select-specimens">
              {ownership &&
                ownership.map((item, index) => {
                  const discardItem = selectedDiscardItems.find(selectedItem =>
                    isFemale ? selectedItem.embryoStrawId === item.embryoStrawId : selectedItem.specimenId === item.specimenId,
                  );
                  const flushMating: string = String(
                    item.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ??
                      '-',
                  );

                  return (
                    <div
                      className={`card max-width ${handleErrors(discardItem, item)}`}
                      key={`specimen-row:${item.specimenId}-straw:${item.embryoStrawId ?? 'none'}-index:${index}`}>
                      <div className="selected-specimens-row">
                        <div className="input-check">{inputCheckbox(discardItem, index)}</div>
                        <div className="select-specimens">
                          <div className="card-content-section">
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                              <label>{`${item.animal?.code} - ${item.animal?.name}`}</label>
                            </div>
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                              <label>{`${item.specimenType?.name}`}</label>
                            </div>
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.LOT_NO}: `}</label>
                              <label>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</label>
                            </div>
                            <div className="right column">
                              <label>{`${TABLE_HEADER_CONSTANTS.FLUSH_MATING}: `}</label>
                              <label>{flushMating}</label>
                            </div>
                          </div>
                          <div className="card-content-section">
                            {isFemale ? (
                              <>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                                  <label>
                                    {`${item.embryoStrawCode ?? '-'}`}{' '}
                                    {isFemale && (
                                      <Tooltip>
                                        {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`} <br />
                                        {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                      </Tooltip>
                                    )}
                                  </label>
                                </div>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                                  <label>{`${item.embryoStrawNumber ?? '-'}`}</label>
                                </div>
                              </>
                            ) : (
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: `}</label>
                                <label>{`${item.quality}`}</label>
                              </div>
                            )}
                            <div className="left column ">
                              <label>
                                {`${isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}
                              </label>
                              <label>{`${item.quantityAvailable}`}</label>
                            </div>
                            <div className="right center column">
                              <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}:`}</label>
                              {inputQuantityElement(discardItem, index)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              <div className="total-quantity">
                <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp; </label>
                {totalDiscardQuantity}
              </div>
            </div>
            <br />

            <h2>{LABEL_CONSTANTS.ITEM_STORAGE_LOCATIONS}</h2>
            <div className="use-storage-locations">
              <div className="desk-storage-locations-table">
                <div className="specimen-table">
                  <table>
                    <thead>
                      <tr>
                        <th>{TABLE_HEADER_CONSTANTS.LOCATION}</th>
                        <th>{TABLE_HEADER_CONSTANTS.ITEM}</th>
                        <th>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</th>
                        {isFemale && <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>}
                        <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSpecimens.length === 0 ? (
                        <>
                          <tr>
                            <td colSpan={isFemale ? 5 : 4} className="storage-locations-placeholder">
                              {LABEL_CONSTANTS.SELECT_SPECIMEN_TO_VIEW_STORAGE_LOCATIONS}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={isFemale ? 4 : 3}></td>
                            <td colSpan={1}>
                              <div className="quantity storage-total">0</div>
                            </td>
                          </tr>
                        </>
                      ) : (
                        <>
                          {selectedSpecimens.map(({ specimen, key }) =>
                            specimen.specimenLocations?.map(location => renderLocationTableRows(specimen, key, location)),
                          )}
                          <tr className={totalLocationDiscardAmounts > totalDiscardQuantity ? 'invalid-row' : ''}>
                            <td colSpan={isFemale ? 4 : 3}></td>
                            <td colSpan={1}>
                              <div className="quantity storage-total">{totalLocationDiscardAmounts}</div>
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="xs-storage-locations-table">
                {selectedSpecimens.length === 0 ? (
                  <div className="storage-locations-empty-card">
                    <p>{LABEL_CONSTANTS.SELECT_SPECIMEN_TO_VIEW_STORAGE_LOCATIONS}</p>
                  </div>
                ) : (
                  <>
                    {selectedSpecimens.map(({ specimen, key }) =>
                      specimen.specimenLocations?.map(location => renderLocationMobileRow(specimen, key, location)),
                    )}
                  </>
                )}
                <div className="storage-total">
                  <label>{LABEL_CONSTANTS.SELECTED_QTY}:&nbsp;</label>
                  {totalLocationDiscardAmounts}
                </div>
              </div>
            </div>
            <br />

            <div className="form-row expanded-text-area">
              <label htmlFor="notes">{LABEL_CONSTANTS.NOTES}</label>
              <textarea
                id="notes"
                name="notes"
                placeholder={LABEL_CONSTANTS.NOTES}
                onChange={e => setNotes(e.target.value)}
                rows={4}
                cols={50}
              />
            </div>
            <br />
            <div className="submit-button">
              <button className={`button green small ${!isValid() && 'disabled'}`}>{BUTTON_CONSTANTS.SUBMIT}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OnFarmDiscard;
